import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListLedgerAccounts,
  useCreateLedgerAccount,
  useUpdateLedgerAccount,
  useListCostCentres,
  useCreateCostCentre,
  useUpdateCostCentre,
  getListLedgerAccountsQueryKey,
  getListCostCentresQueryKey,
} from "@workspace/api-client-react";
import type { LedgerAccount, LedgerAccountType, PaymentSourceKind } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Info, X } from "lucide-react";

const ACCOUNT_TYPES: LedgerAccountType[] = ["asset", "liability", "equity", "income", "expense"];
const PAYMENT_SOURCE_KINDS: PaymentSourceKind[] = ["bank", "cash", "petty_cash", "partner"];

interface NewAccountForm {
  code: string;
  name: string;
  type: LedgerAccountType;
  normalBalance: "debit" | "credit";
  paymentSourceKind: PaymentSourceKind | "none";
}

const EMPTY_ACCOUNT_FORM: NewAccountForm = { code: "", name: "", type: "expense", normalBalance: "debit", paymentSourceKind: "none" };

interface NewCostCentreForm {
  code: string;
  name: string;
}

/**
 * A6 + A4 — chart of accounts (with the `paymentSourceKind` funding-source
 * control) + cost centres, one click from `/ledger`. Super-admin only:
 * `ledger-masters.ts` gates every write here with `requireSuperAdmin`.
 * Only `UNALLOC` and the four seeded funding accounts ship out of the box —
 * this screen is the prerequisite for correct books, not a settings
 * backwater, so the callout below is deliberately loud on first visit.
 */
export function MasterDataPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: accounts, isLoading: accountsLoading } = useListLedgerAccounts({});
  const { data: costCentres, isLoading: costCentresLoading } = useListCostCentres();
  const createAccount = useCreateLedgerAccount();
  const updateAccount = useUpdateLedgerAccount();
  const createCostCentre = useCreateCostCentre();
  const updateCostCentre = useUpdateCostCentre();

  const [accountForm, setAccountForm] = useState<NewAccountForm>(EMPTY_ACCOUNT_FORM);
  const [ccForm, setCcForm] = useState<NewCostCentreForm>({ code: "", name: "" });
  const [calloutDismissed, setCalloutDismissed] = useState(false);

  const fundingAccountCount = (accounts ?? []).filter((a) => a.paymentSourceKind != null).length;
  const onlyUnallocSeeded = (costCentres ?? []).length === 1 && costCentres?.[0]?.code === "UNALLOC";
  const onlySeededFunding = fundingAccountCount > 0 && fundingAccountCount <= 4;
  const showCallout = !calloutDismissed && (onlyUnallocSeeded || onlySeededFunding);

  function refreshAccounts() {
    qc.invalidateQueries({ queryKey: getListLedgerAccountsQueryKey() });
  }
  function refreshCostCentres() {
    qc.invalidateQueries({ queryKey: getListCostCentresQueryKey() });
  }

  async function submitNewAccount() {
    if (!accountForm.code.trim() || !accountForm.name.trim()) {
      toast({ title: "Code and name are required.", variant: "destructive" });
      return;
    }
    try {
      await createAccount.mutateAsync({
        data: {
          code: accountForm.code.trim(),
          name: accountForm.name.trim(),
          type: accountForm.type,
          normalBalance: accountForm.normalBalance,
          paymentSourceKind: accountForm.paymentSourceKind === "none" ? null : accountForm.paymentSourceKind,
        },
      });
      refreshAccounts();
      toast({ title: `Account ${accountForm.code} created.` });
      setAccountForm(EMPTY_ACCOUNT_FORM);
    } catch (err) {
      // Surface the API's own 400 verbatim (e.g. the range guard rail) — an
      // operator who flags the wrong account as a funding source needs the
      // DB/route's exact refusal text, not a generic "failed" toast.
      toast({ title: err instanceof Error ? err.message : "Could not create account.", variant: "destructive" });
    }
  }

  async function toggleAccountActive(a: LedgerAccount) {
    try {
      await updateAccount.mutateAsync({ id: a.id, data: { active: !a.active } });
      refreshAccounts();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not update account.", variant: "destructive" });
    }
  }

  async function submitNewCostCentre() {
    if (!ccForm.code.trim() || !ccForm.name.trim()) {
      toast({ title: "Code and name are required.", variant: "destructive" });
      return;
    }
    try {
      await createCostCentre.mutateAsync({ data: { code: ccForm.code.trim(), name: ccForm.name.trim() } });
      refreshCostCentres();
      toast({ title: `Cost centre "${ccForm.name}" created.` });
      setCcForm({ code: "", name: "" });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not create cost centre.", variant: "destructive" });
    }
  }

  async function toggleCostCentreActive(id: number, active: boolean, isBalanceSheet: boolean) {
    if (isBalanceSheet && active) {
      toast({ title: "UNALLOC cannot be deactivated — every balance-sheet leg posts to it.", variant: "destructive" });
      return;
    }
    try {
      await updateCostCentre.mutateAsync({ id, data: { active: !active } });
      refreshCostCentres();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not update cost centre.", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-6">
      {showCallout && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between">
            <span>Only starter master data exists so far</span>
            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setCalloutDismissed(true)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </AlertTitle>
          <AlertDescription>
            {onlyUnallocSeeded && "Only the UNALLOC cost centre is seeded — every early bill lands unallocated until real centres exist. "}
            {onlySeededFunding && "Only the seeded starter bank/cash accounts exist — create your real ones before recording the first payment."}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Chart of accounts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
            <div>
              <label className="text-[11px] font-semibold">Code</label>
              <Input value={accountForm.code} onChange={(e) => setAccountForm((f) => ({ ...f, code: e.target.value }))} placeholder="e.g. 10102" />
            </div>
            <div>
              <label className="text-[11px] font-semibold">Name</label>
              <Input value={accountForm.name} onChange={(e) => setAccountForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. ICICI Current A/c" />
            </div>
            <div>
              <label className="text-[11px] font-semibold">Type</label>
              <Select value={accountForm.type} onValueChange={(v) => setAccountForm((f) => ({ ...f, type: v as LedgerAccountType }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-semibold">Normal balance</label>
              <Select value={accountForm.normalBalance} onValueChange={(v) => setAccountForm((f) => ({ ...f, normalBalance: v as "debit" | "credit" }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">Debit</SelectItem>
                  <SelectItem value="credit">Credit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-semibold">Funding source?</label>
              <Select
                value={accountForm.paymentSourceKind}
                onValueChange={(v) => setAccountForm((f) => ({ ...f, paymentSourceKind: v as PaymentSourceKind | "none" }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not a funding source</SelectItem>
                  {PAYMENT_SOURCE_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            bank / cash / petty cash may only be flagged on an asset account coded 10100-10199; partner may only be
            flagged on an equity account coded 30200-30299. Any other combination is refused by the API.
          </p>
          <Button size="sm" onClick={submitNewAccount} disabled={createAccount.isPending}>
            {createAccount.isPending ? "Creating…" : "Add account"}
          </Button>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Funding kind</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accountsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground h-16">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : (
                (accounts ?? []).map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono text-sm">{a.code}</TableCell>
                    <TableCell className="text-sm">{a.name}</TableCell>
                    <TableCell className="text-sm">{a.type}</TableCell>
                    <TableCell>{a.paymentSourceKind ? <Badge variant="secondary">{a.paymentSourceKind}</Badge> : "—"}</TableCell>
                    <TableCell>{a.active ? <Badge variant="secondary">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => toggleAccountActive(a)}>
                        {a.active ? "Deactivate" : "Reactivate"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost centres</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[11px] font-semibold">Code</label>
              <Input value={ccForm.code} onChange={(e) => setCcForm((f) => ({ ...f, code: e.target.value }))} placeholder="e.g. FRONT_DESK" />
            </div>
            <div>
              <label className="text-[11px] font-semibold">Name</label>
              <Input value={ccForm.name} onChange={(e) => setCcForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Front Desk" />
            </div>
            <Button size="sm" onClick={submitNewCostCentre} disabled={createCostCentre.isPending}>
              {createCostCentre.isPending ? "Creating…" : "Add cost centre"}
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {costCentresLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground h-16">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : (
                (costCentres ?? []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-sm">{c.code}</TableCell>
                    <TableCell className="text-sm">
                      {c.name} {c.isBalanceSheet && <Badge variant="outline" className="ml-1">balance sheet</Badge>}
                    </TableCell>
                    <TableCell>{c.active ? <Badge variant="secondary">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => toggleCostCentreActive(c.id, c.active, c.isBalanceSheet)}>
                        {c.active ? "Deactivate" : "Reactivate"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
